import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, ChevronDown, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const ELEMENTS = ['Agua', 'Fuego', 'Aire', 'Éter', 'Agua-Tierra', 'Tierra', 'Sol'];
const ELEMENT_DAYS = { Agua: 'Lunes', Fuego: 'Martes', Aire: 'Miércoles', 'Éter': 'Jueves', 'Agua-Tierra': 'Viernes', Tierra: 'Sábado', Sol: 'Domingo' };
const EVENT_TYPES = [
  { value: 'sabbat', label: 'Sabbat Celta' },
  { value: 'eclipse', label: 'Eclipse' },
  { value: 'retrogrado', label: 'Mercurio Retrógrado' },
  { value: 'retrogrado_fin', label: 'Mercurio Directo' },
  { value: 'custom', label: 'Personalizado' },
];

const AdminAltarPresente = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState('decretos');
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await api.get('/admin/campus/altar/content');
      setData(res.data);
    } catch { toast.error('Error al cargar datos del altar'); }
    setLoading(false);
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#c9a227]" /></div>;
  if (!data) return <p className="text-sm text-gray-400">Error al cargar</p>;

  const SECTIONS = [
    { id: 'decretos', label: 'Decretos', count: Object.values(data.decretos || {}).flat().length },
    { id: 'somaticas', label: 'Somáticas', count: Object.values(data.somaticas || {}).flat().length },
    { id: 'eventos', label: 'Calendario', count: (data.eventos || []).length },
    { id: 'plantas', label: 'Plantas', count: (data.plantas || []).length },
  ];

  return (
    <div className="space-y-4" data-testid="admin-altar">
      <div className="flex gap-1 bg-[#faf6f0] rounded-lg p-1 border border-[#e8e2d8]">
        {SECTIONS.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${section === s.id ? 'bg-white shadow-sm text-[#5c4a2a]' : 'text-[#8b7340]/50 hover:text-[#5c4a2a]'}`}
            data-testid={`altar-section-${s.id}`}
          >
            {s.label} ({s.count})
          </button>
        ))}
      </div>

      {section === 'decretos' && <TextListEditor type="decretos" data={data.decretos} onUpdate={loadData} label="Decreto" />}
      {section === 'somaticas' && <TextListEditor type="somaticas" data={data.somaticas} onUpdate={loadData} label="Práctica somática" />}
      {section === 'eventos' && <EventosEditor eventos={data.eventos} onUpdate={loadData} />}
      {section === 'plantas' && <PlantasEditor plantas={data.plantas} onUpdate={loadData} />}
    </div>
  );
};

/* ═══ TEXT LIST EDITOR (Decretos + Somáticas) ═══ */
const TextListEditor = ({ type, data, onUpdate, label }) => {
  const [openElement, setOpenElement] = useState('Agua');
  const [items, setItems] = useState({});
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState('');

  useEffect(() => { setItems(data || {}); }, [data]);

  const saveElement = async (element) => {
    setSaving(true);
    try {
      await api.put(`/admin/campus/altar/${type}`, { element, items: items[element] || [] });
      toast.success(`${label}s de ${element} guardados`);
      onUpdate();
    } catch { toast.error('Error al guardar'); }
    setSaving(false);
  };

  const addItem = (element) => {
    if (!newItem.trim()) return;
    setItems(prev => ({ ...prev, [element]: [...(prev[element] || []), newItem.trim()] }));
    setNewItem('');
  };

  const removeItem = (element, idx) => {
    setItems(prev => ({ ...prev, [element]: prev[element].filter((_, i) => i !== idx) }));
  };

  const updateItem = (element, idx, value) => {
    setItems(prev => {
      const updated = [...(prev[element] || [])];
      updated[idx] = value;
      return { ...prev, [element]: updated };
    });
  };

  return (
    <div className="space-y-2">
      {ELEMENTS.map(el => {
        const isOpen = openElement === el;
        const elItems = items[el] || [];
        return (
          <div key={el} className="border border-[#e8e2d8] rounded-lg bg-white overflow-hidden">
            <button onClick={() => setOpenElement(isOpen ? '' : el)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#faf6f0]/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                {isOpen ? <ChevronDown className="w-4 h-4 text-[#c9a227]" /> : <ChevronRight className="w-4 h-4 text-[#8b7340]/30" />}
                <span className="text-sm font-medium text-[#5c4a2a]">{el}</span>
                <span className="text-xs text-[#8b7340]/40">{ELEMENT_DAYS[el]}</span>
              </div>
              <span className="text-xs text-[#8b7340]/40">{elItems.length} {label.toLowerCase()}s</span>
            </button>
            {isOpen && (
              <div className="px-4 pb-4 space-y-2 border-t border-[#e8e2d8]/60">
                {elItems.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-start mt-2">
                    <span className="text-xs text-[#8b7340]/30 pt-2 w-5 flex-shrink-0">{idx + 1}</span>
                    <textarea value={item} onChange={e => updateItem(el, idx, e.target.value)}
                      className="flex-1 text-sm text-[#5c4a2a] border border-[#e8e2d8] rounded px-3 py-2 resize-none focus:outline-none focus:border-[#c9a227]/40"
                      rows={2}
                    />
                    <button onClick={() => removeItem(el, idx)} className="text-red-300 hover:text-red-500 pt-2">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <div className="flex gap-2 mt-3">
                  <input value={newItem} onChange={e => setNewItem(e.target.value)} placeholder={`Nuevo ${label.toLowerCase()}...`}
                    className="flex-1 text-sm border border-[#e8e2d8] rounded px-3 py-2 focus:outline-none focus:border-[#c9a227]/40"
                    onKeyDown={e => e.key === 'Enter' && addItem(el)}
                  />
                  <button onClick={() => addItem(el)} className="text-[#c9a227] hover:text-[#8b6914] px-2"><Plus className="w-4 h-4" /></button>
                </div>
                <button onClick={() => saveElement(el)} disabled={saving}
                  className="flex items-center gap-2 mt-2 px-4 py-1.5 rounded-lg text-xs font-medium bg-[#c9a227]/10 text-[#8b6914] hover:bg-[#c9a227]/20 border border-[#c9a227]/20 transition-all"
                >
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  Guardar {el}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ═══ EVENTOS / CALENDARIO EDITOR ═══ */
const EventosEditor = ({ eventos, onUpdate }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ day: '', month: '', name: '', desc: '', type: 'custom' });
  const [saving, setSaving] = useState(false);

  const addEvento = async () => {
    if (!form.name || !form.day || !form.month) return toast.error('Completa día, mes y nombre');
    setSaving(true);
    try {
      await api.post('/admin/campus/altar/eventos', { ...form, day: parseInt(form.day), month: parseInt(form.month) });
      toast.success('Evento añadido');
      setForm({ day: '', month: '', name: '', desc: '', type: 'custom' });
      setAdding(false);
      onUpdate();
    } catch { toast.error('Error al añadir'); }
    setSaving(false);
  };

  const deleteEvento = async (id) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    try {
      await api.delete(`/admin/campus/altar/eventos/${id}`);
      toast.success('Evento eliminado');
      onUpdate();
    } catch { toast.error('Error al eliminar'); }
  };

  const toggleEvento = async (ev) => {
    try {
      await api.put(`/admin/campus/altar/eventos/${ev.id}`, { active: !ev.active });
      onUpdate();
    } catch { toast.error('Error'); }
  };

  const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const sorted = [...(eventos || [])].sort((a, b) => (a.month * 100 + a.day) - (b.month * 100 + b.day));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#8b7340]/50">Sabbats, eclipses, retrógrados y eventos personalizados</p>
        <button onClick={() => setAdding(!adding)} className="flex items-center gap-1 text-xs text-[#c9a227] hover:text-[#8b6914]">
          <Plus className="w-3.5 h-3.5" /> Añadir evento
        </button>
      </div>

      {adding && (
        <div className="p-4 border border-[#c9a227]/20 rounded-lg bg-[#faf6f0] space-y-3">
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#8b7340]/50">Día</label>
              <input type="number" min="1" max="31" value={form.day} onChange={e => setForm({ ...form, day: e.target.value })}
                className="w-full mt-1 text-sm border border-[#e8e2d8] rounded px-3 py-1.5 focus:outline-none focus:border-[#c9a227]/40" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#8b7340]/50">Mes</label>
              <input type="number" min="1" max="12" value={form.month} onChange={e => setForm({ ...form, month: e.target.value })}
                className="w-full mt-1 text-sm border border-[#e8e2d8] rounded px-3 py-1.5 focus:outline-none focus:border-[#c9a227]/40" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#8b7340]/50">Tipo</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                className="w-full mt-1 text-sm border border-[#e8e2d8] rounded px-3 py-1.5 focus:outline-none focus:border-[#c9a227]/40">
                {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#8b7340]/50">Nombre</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full mt-1 text-sm border border-[#e8e2d8] rounded px-3 py-1.5 focus:outline-none focus:border-[#c9a227]/40" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#8b7340]/50">Descripción</label>
            <input value={form.desc} onChange={e => setForm({ ...form, desc: e.target.value })}
              className="w-full mt-1 text-sm border border-[#e8e2d8] rounded px-3 py-1.5 focus:outline-none focus:border-[#c9a227]/40" />
          </div>
          <button onClick={addEvento} disabled={saving}
            className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-medium bg-[#c9a227]/10 text-[#8b6914] hover:bg-[#c9a227]/20 border border-[#c9a227]/20">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Guardar
          </button>
        </div>
      )}

      <div className="space-y-1">
        {sorted.map(ev => (
          <div key={ev.id} className={`flex items-center gap-3 px-3 py-2 rounded-lg border text-sm ${ev.active !== false ? 'bg-white border-[#e8e2d8]' : 'bg-gray-50 border-gray-200 opacity-50'}`}>
            <button onClick={() => toggleEvento(ev)} className={`w-2.5 h-2.5 rounded-full flex-shrink-0 border ${ev.active !== false ? 'bg-[#c9a227] border-[#c9a227]' : 'bg-gray-300 border-gray-300'}`} title={ev.active !== false ? 'Activo' : 'Inactivo'} />
            <span className="text-[#8b7340]/60 text-xs w-12 flex-shrink-0 font-mono">{ev.day}/{MONTHS[(ev.month || 1) - 1]}</span>
            <span className="text-[10px] uppercase tracking-wider text-[#8b7340]/40 w-20 flex-shrink-0">{ev.type}</span>
            <span className="text-[#5c4a2a] font-medium flex-1">{ev.name}</span>
            <span className="text-[#8b7340]/40 text-xs flex-1 truncate">{ev.desc}</span>
            <button onClick={() => deleteEvento(ev.id)} className="text-red-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ═══ PLANTAS EDITOR ═══ */
const PlantasEditor = ({ plantas, onUpdate }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', latin_name: '', properties: '', tradition: '', week_start: '', image_url: '', element: '', somatic_use: '' });
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const reseedPlantas = async () => {
    if (!window.confirm('Esto reemplazará todas las plantas con los datos predefinidos (30 plantas con imágenes). ¿Continuar?')) return;
    setSyncing(true);
    try {
      const res = await api.post('/admin/campus/altar/plantas/reseed');
      toast.success(`${res.data.count} plantas sincronizadas correctamente`);
      onUpdate();
    } catch { toast.error('Error al sincronizar plantas'); }
    setSyncing(false);
  };

  const addPlanta = async () => {
    if (!form.name || !form.week_start) return toast.error('Completa nombre y semana de inicio');
    setSaving(true);
    try {
      await api.post('/admin/campus/altar/plantas', form);
      toast.success('Planta añadida');
      setForm({ name: '', latin_name: '', properties: '', tradition: '', week_start: '', image_url: '', element: '', somatic_use: '' });
      setAdding(false);
      onUpdate();
    } catch { toast.error('Error al añadir'); }
    setSaving(false);
  };

  const deletePlanta = async (id) => {
    if (!window.confirm('¿Eliminar esta planta?')) return;
    try {
      await api.delete(`/admin/campus/altar/plantas/${id}`);
      toast.success('Planta eliminada');
      onUpdate();
    } catch { toast.error('Error'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-[#8b7340]/50">Plantas semanales: el Altar mostrará la que corresponda a la fecha actual</p>
        <div className="flex items-center gap-2">
          <button onClick={reseedPlantas} disabled={syncing} data-testid="reseed-plantas-btn"
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium bg-[#6a8a6a]/10 text-[#4a6a4a] hover:bg-[#6a8a6a]/20 border border-[#6a8a6a]/20 transition-all">
            {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Sincronizar 30 plantas
          </button>
          <button onClick={() => setAdding(!adding)} className="flex items-center gap-1 text-xs text-[#c9a227] hover:text-[#8b6914]">
            <Plus className="w-3.5 h-3.5" /> Añadir planta
          </button>
        </div>
      </div>

      {adding && (
        <div className="p-4 border border-[#c9a227]/20 rounded-lg bg-[#faf6f0] space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#8b7340]/50">Nombre</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Romero" className="w-full mt-1 text-sm border border-[#e8e2d8] rounded px-3 py-1.5 focus:outline-none focus:border-[#c9a227]/40" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#8b7340]/50">Nombre latín</label>
              <input value={form.latin_name} onChange={e => setForm({ ...form, latin_name: e.target.value })}
                placeholder="Rosmarinus officinalis" className="w-full mt-1 text-sm border border-[#e8e2d8] rounded px-3 py-1.5 focus:outline-none focus:border-[#c9a227]/40" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#8b7340]/50">Propiedades</label>
            <textarea value={form.properties} onChange={e => setForm({ ...form, properties: e.target.value })}
              placeholder="Protección, claridad mental, fortaleza del sistema nervioso" rows={2}
              className="w-full mt-1 text-sm border border-[#e8e2d8] rounded px-3 py-1.5 focus:outline-none focus:border-[#c9a227]/40 resize-none" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#8b7340]/50">Uso somático</label>
            <textarea value={form.somatic_use} onChange={e => setForm({ ...form, somatic_use: e.target.value })}
              placeholder="Frota unas hojas entre tus manos e inhala..." rows={2}
              className="w-full mt-1 text-sm border border-[#e8e2d8] rounded px-3 py-1.5 focus:outline-none focus:border-[#c9a227]/40 resize-none" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#8b7340]/50">Tradición</label>
              <input value={form.tradition} onChange={e => setForm({ ...form, tradition: e.target.value })}
                placeholder="Druida · purificación" className="w-full mt-1 text-sm border border-[#e8e2d8] rounded px-3 py-1.5 focus:outline-none focus:border-[#c9a227]/40" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#8b7340]/50">Elemento</label>
              <input value={form.element} onChange={e => setForm({ ...form, element: e.target.value })}
                placeholder="Fuego" className="w-full mt-1 text-sm border border-[#e8e2d8] rounded px-3 py-1.5 focus:outline-none focus:border-[#c9a227]/40" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#8b7340]/50">Semana inicio</label>
              <input type="date" value={form.week_start} onChange={e => setForm({ ...form, week_start: e.target.value })}
                className="w-full mt-1 text-sm border border-[#e8e2d8] rounded px-3 py-1.5 focus:outline-none focus:border-[#c9a227]/40" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#8b7340]/50">URL imagen (grimorio)</label>
            <input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })}
              placeholder="https://res.cloudinary.com/..." className="w-full mt-1 text-sm border border-[#e8e2d8] rounded px-3 py-1.5 focus:outline-none focus:border-[#c9a227]/40" />
            {form.image_url && (
              <div className="mt-2 w-16 h-16 rounded border border-[#e8e2d8] overflow-hidden">
                <img src={form.image_url} alt="preview" className="w-full h-full object-cover" />
              </div>
            )}
          </div>
          <button onClick={addPlanta} disabled={saving}
            className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-medium bg-[#c9a227]/10 text-[#8b6914] hover:bg-[#c9a227]/20 border border-[#c9a227]/20">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Guardar planta
          </button>
        </div>
      )}

      {(plantas || []).length === 0 && !adding && (
        <p className="text-center text-sm text-[#8b7340]/30 py-8 italic">No hay plantas aún. Añade la primera.</p>
      )}

      <div className="space-y-2">
        {(plantas || []).sort((a, b) => (a.week_start || '').localeCompare(b.week_start || '')).map(p => (
          <div key={p.id} className="flex items-center gap-3 px-4 py-3 rounded-lg border border-[#e8e2d8] bg-white" data-testid={`planta-${p.id}`}>
            {p.image_url ? (
              <div className="w-10 h-10 rounded overflow-hidden border border-[#e8e2d8] flex-shrink-0">
                <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
              </div>
            ) : (
              <span className="text-xl">🌿</span>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#5c4a2a]">{p.name} <span className="text-xs text-[#8b7340]/40 italic">{p.latin_name}</span></p>
              <p className="text-xs text-[#8b7340]/50 truncate">{p.properties}</p>
            </div>
            <span className="text-[10px] text-[#8b7340]/40 flex-shrink-0">desde {p.week_start}</span>
            {p.tradition && <span className="text-[9px] uppercase tracking-wider text-[rgba(106,138,106,0.7)] bg-[rgba(106,138,106,0.08)] border border-[rgba(106,138,106,0.2)] px-2 py-0.5 rounded-full flex-shrink-0">{p.tradition}</span>}
            <button onClick={() => deletePlanta(p.id)} className="text-red-300 hover:text-red-500 flex-shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminAltarPresente;
